/**
 * The practice scene: a started attempt played against its customer, or
 * one moment of an assessed practice played again.
 */

import { CoachingNavParams } from '@euler/app/flows/coaching';
import { EmptyView } from '@euler/components/EmptyView';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { CoachingAttemptStatus } from '@euler/model/coaching';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useMemo } from 'react';
import { useMutePreference } from '../../practice/functions/useMutePreference';
import {
  PracticeOpening,
  useOpenPracticeScene,
} from '../../practice/functions/useOpenPracticeScene';
import { PracticeSceneView } from '../../practice/scene/PracticeSceneView';
import { PracticeSceneSkeleton } from '../../practice/scene/components/PracticeSceneSkeleton';

export const CoachingPracticeScreen = wrapNavigatorScreen(
  ({ attemptId, momentTurn }: { attemptId: string; momentTurn?: number }) => {
    const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
    const { speak$, toggle, loaded } = useMutePreference();
    // From a link the turn may be anything: only a line number replays.
    const replayTurn =
      momentTurn != null && Number.isSafeInteger(momentTurn) && momentTurn >= 1
        ? momentTurn
        : null;
    const opening = useMemo<PracticeOpening>(
      () =>
        replayTurn != null
          ? { kind: 'replay', attemptId, momentTurn: replayTurn }
          : { kind: 'practice', attemptId },
      [attemptId, replayTurn],
    );
    const { state, reload } = useOpenPracticeScene(
      opening,
      speak$.getValue(),
      loaded,
    );
    if (state.kind === 'loading')
      return <PracticeSceneSkeleton onBack={() => navigation.goBack()} />;
    if (state.kind === 'failed')
      return <EmptyView message="练习暂时没打开" onRetry={reload} />;
    return (
      <PracticeSceneView
        scene={state.scene}
        speak$={speak$}
        onToggleSpeak={toggle}
        onEnded={end =>
          end.status === CoachingAttemptStatus.Abandoned
            ? navigation.goBack()
            : navigation.replace('report', { attemptId })
        }
        onReplayAgain={() =>
          navigation.replace('practice', {
            attemptId,
            momentTurn: replayTurn ?? undefined,
          })
        }
        onBackToReport={() => navigation.goBack()}
        onBack={() => navigation.goBack()}
      />
    );
  },
  { title: '对练', headerShown: false },
);
